import type { ToolCall } from '../types';
import type { ArtifactReference } from '../types/events';
import type { UIEvent } from '../types/ui-events';

/** Maps a MIME type to an Artifact contentType */
export function mimeToArtifactContentType(mime: string): 'image' | 'html' | 'markdown' | 'code' | 'text' {
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'text/html') return 'html';
  if (mime === 'text/markdown') return 'markdown';
  return 'text';
}

/**
 * Dispatches a ui_event tool call to the canvas.
 *
 * For show_artifact events with only an artifactId, enriches the event data
 * from the artifact cache (populated by prior tool_result events).
 *
 * The tool call is still included in content (not filtered) so it renders
 * in the chain-of-thought section for transparency.
 */
export function dispatchIfUIEvent(
  tc: ToolCall,
  dispatchUIEvent?: (event: UIEvent) => void,
  dispatchedIds?: Set<string>,
  artifactCache?: Map<string, ArtifactReference>,
  getToolName?: (nameOrId: string) => string,
): void {
  // Check both the raw tool name (what the backend sends, e.g. 'ui_event') and
  // the resolved display name in case the backend sends a toolId that maps to it.
  const resolvedName = getToolName?.(tc.toolName) ?? tc.toolName;
  if (tc.toolName !== 'ui_event' && resolvedName !== 'ui_event') return;
  if (!dispatchUIEvent || !dispatchedIds || dispatchedIds.has(tc.toolCallId)) return;

  // Normalize: if input arrived as a JSON string (double-serialized during EventBridge transit), parse it first
  let rawInput = tc.input as unknown;
  if (typeof rawInput === 'string') {
    try {
      rawInput = JSON.parse(rawInput as string);
      console.log('[artifact-dispatch] parsed string input for', tc.toolCallId);
    } catch {
      console.log('[artifact-dispatch] unparseable string input:', tc.toolCallId);
      return;
    }
  }

  // Guard against null/undefined/non-object input — don't add to dispatchedIds so it can be retried
  // when the completed event brings the full tool call data.
  if (!rawInput || typeof rawInput !== 'object') {
    console.log('[artifact-dispatch] no usable input, will retry:', tc.toolCallId);
    return;
  }

  try {
    const eventData = rawInput as { eventType?: string; data?: Record<string, unknown> };
    if (!eventData.eventType || !eventData.data) {
      // Don't add to dispatchedIds — input might be enriched later by completed event
      console.log('[artifact-dispatch] missing eventType or data:', tc.toolCallId);
      return;
    }

    let data = eventData.data;

    // Enrich show_artifact events from the artifact cache
    if (eventData.eventType === 'show_artifact' && data.artifactId && artifactCache) {
      const ref = artifactCache.get(data.artifactId as string);
      if (ref) {
        const contentType = mimeToArtifactContentType(ref.contentType);
        data = {
          ...data,
          title: data.title ?? ref.filename ?? 'Artifact',
          contentType,
          url: ref.url,
          mimeType: ref.contentType,
          filename: ref.filename,
        };
        console.log('[artifact-dispatch] enriched from cache:', tc.toolCallId, { contentType, url: !!ref.url });
      } else {
        console.log('[artifact-dispatch] no cache entry for artifactId:', data.artifactId);
      }
    }

    console.log('[artifact-dispatch] dispatching:', tc.toolCallId, eventData.eventType);
    dispatchUIEvent({ eventType: eventData.eventType, data });
    // Only mark as dispatched AFTER successful dispatch
    dispatchedIds.add(tc.toolCallId);
  } catch (err) {
    // Don't add to dispatchedIds — allows retry on next yieldContent
    console.warn('[useAsyncInvokeAdapter] Failed to dispatch ui_event:', err);
  }
}
